import { useToastStore } from '@/stores/toastStore'
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useState, useEffect, useRef } from 'react'

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

const iconColorMap = {
  success: 'text-emerald-400',
  error: 'text-red-400',
  warning: 'text-amber-400',
  info: 'text-blue-400',
}

export function GlobalToast() {
  const current = useToastStore((s) => s.current)
  const dismiss = useToastStore((s) => s.dismiss)
  const [isExiting, setIsExiting] = useState(false)
  const [displayed, setDisplayed] = useState(current)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (current) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current)
      setIsExiting(false)
      setDisplayed(current)
    } else if (displayed) {
      setIsExiting(true)
      const timer = setTimeout(() => {
        setDisplayed(null)
        setIsExiting(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [current])

  if (!displayed) return null

  const Icon = iconMap[displayed.type]

  const handleDismiss = () => {
    setIsExiting(true)
    dismissTimerRef.current = setTimeout(dismiss, 200)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      // top: 노치/Dynamic Island 영역에 가려지지 않도록 safe-area-inset-top 보장
      style={{ top: 'max(1rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))' }}
      className={clsx(
        'fixed left-4 right-4 lg:left-1/2 lg:-translate-x-1/2 lg:max-w-md',
        'z-50 pointer-events-none'
      )}
    >
      <div
        className={clsx(
          'pointer-events-auto flex items-center gap-3 px-4 py-3',
          'bg-gray-900 dark:bg-gray-800 text-white rounded-xl shadow-lg',
          'border border-white/10',
          isExiting
            ? 'animate-[slideOutTop_0.2s_ease-in_forwards]'
            : 'animate-[slideInFromTop_0.3s_ease-out]'
        )}
      >
        <Icon className={clsx('w-5 h-5 flex-shrink-0', iconColorMap[displayed.type])} />
        <span className="flex-1 text-sm">{displayed.message}</span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="닫기"
          className="flex-shrink-0 p-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4 opacity-60" />
        </button>
      </div>
    </div>
  )
}
